// Pure helpers for the Custom Analytics board. The backend (singabot custom-metrics) owns the
// charts and returns them with their data; these helpers turn that data into what the cards draw
// (chart.js 2.x data, metric tiles, legend and filter lists) and handle the little date and
// layout work the FE still does. Contract: docs 03-api-contract.md / 05-fe-handoff.md.
//
// Chart data comes in two shapes, switched on `viz_type`:
//   metric / pie  { values: [{ value, count }], other_count, total, hidden: [{ value, count }] }
//   bar           { interval, buckets: [iso], series: [{ value, counts }], other: { counts }|null,
//                   total, hidden }
// donut reads the metric / pie shape; area, spark_bar and spark_area read the bar shape.

// Types the backend stores, in the order of the Botpress type menu.
export const ENABLED_TYPES = [
  'area',
  'donut',
  'bar',
  'pie',
  'metric',
  'spark_bar',
  'spark_area',
];

// Types whose data has `buckets` and `series` (the others have `values`).
const BAR_SHAPE_TYPES = ['bar', 'area', 'spark_bar', 'spark_area'];
export const isBarShape = vizType => BAR_SHAPE_TYPES.includes(vizType);

// Smallest a chart card can be on the grid (12 columns, 40px rows with 12px gaps). Botpress's
// smallest card is 212px tall; 5 rows is 248px, the nearest size above it (4 rows is 196px).
export const MIN_CARD_W = 3;
export const MIN_CARD_H = 5;

export const DATE_PRESETS = [
  { key: 'last_year' },
  { key: 'last_6_months' },
  { key: 'last_90_days' },
  { key: 'last_30_days' },
  { key: 'last_7_days' },
  { key: 'last_day' },
];

export const DEFAULT_PRESET = 'last_30_days';
export const MAX_CUSTOM_DAYS = 400;
// A chart can be limited to at most this many inboxes (the backend rejects more).
export const MAX_INBOXES = 100;
// A chart that compares metrics (`series_by: 'key'`) can hold at most this many.
export const MAX_METRIC_KEYS = 10;
export const OTHER_LABEL = 'Other';
export const OTHER_COLOR = '#889096';

export const PALETTE = [
  '#2781F6',
  '#F5A524',
  '#17C964',
  '#F31260',
  '#7828C8',
  '#06B7DB',
  '#FF7849',
  '#9353D3',
  '#45D483',
  '#338EF7',
  '#F7B750',
  '#C20E4D',
];

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const formatNumber = value => Number(value).toLocaleString('en-US');

const paletteIndex = value => {
  let h = 7;
  const text = String(value);
  for (let i = 0; i < text.length; i += 1) {
    h = (h * 31 + text.charCodeAt(i)) % 1000003;
  }
  return h % PALETTE.length;
};

/** A value's own colour: the same in every chart and after every reload. */
export const colorForValue = value => PALETTE[paletteIndex(value)];

/**
 * Colours for every value in one chart. Each value keeps its own colour unless another value in
 * the same chart hashed to it too, in which case the next free colour is used (values are
 * settled in alphabetical order, so counts changing never reshuffles them).
 */
export const colorMap = values => {
  const map = {};
  const taken = new Set();
  [...new Set(values.map(String))].sort().forEach(value => {
    let index = paletteIndex(value);
    for (
      let tries = 0;
      tries < PALETTE.length && taken.has(index);
      tries += 1
    ) {
      index = (index + 1) % PALETTE.length;
    }
    taken.add(index);
    map[value] = PALETTE[index];
  });
  return map;
};

// Every value a chart can show, hidden ones included, so hiding one never recolours the rest.
const valuesIn = data => [
  ...(isBarShape(data?.viz_type)
    ? (data.series || []).map(s => s.value)
    : (data?.values || []).map(v => v.value)),
  ...(data?.hidden || []).map(h => h.value),
];

/**
 * Bucket start (ISO with offset, e.g. 2026-10-14T00:00:00+08:00) to axis label. Read from the
 * string so the browser's own time zone can never shift it.
 */
export const formatBucket = (iso, interval) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(iso));
  if (!match) return String(iso);
  if (interval === 'hour') return `${match[4]}:${match[5]}`;
  return `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}`;
};

/** `2026-10-01` becomes `1 Oct 2026` */
export const formatDateString = value => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!match) return String(value);
  return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
};

const pad = number => String(number).padStart(2, '0');

export const toDateString = date =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Whole days covered by two `YYYY-MM-DD` strings, inclusive. */
export const daysInRange = (from, to) => {
  const parse = value => {
    const [y, m, d] = value.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((parse(to) - parse(from)) / 86400000) + 1;
};

/** Calendar range [Date, Date] to `{ from, to, days }` as local dates. */
export const dateRangeToDays = ([start, end]) => {
  const from = toDateString(start);
  const to = toDateString(end);
  return { from, to, days: daysInRange(from, to) };
};

export const isRangeTooLarge = ({ from, to }) =>
  daysInRange(from, to) > MAX_CUSTOM_DAYS;

/** `Sep 24 – Oct 20` from the range the backend resolved (`to` is exclusive). */
export const rangeLabel = data => {
  if (!data?.from || !data?.to) return '';
  const options = { month: 'short', day: 'numeric' };
  if (data.tz) options.timeZone = data.tz;
  const start = new Date(data.from);
  const end = new Date(Date.parse(data.to) - 1000);
  try {
    const first = start.toLocaleDateString('en-US', options);
    const last = end.toLocaleDateString('en-US', options);
    // A range over a year boundary needs the years, or "Oct 20 – Oct 20" reads as one day.
    const withYear = { ...options, year: 'numeric' };
    const firstYear = start.toLocaleDateString('en-US', withYear);
    const lastYear = end.toLocaleDateString('en-US', withYear);
    if (firstYear.slice(-4) !== lastYear.slice(-4)) {
      return `${firstYear} – ${lastYear}`;
    }
    return first === last ? first : `${first} – ${last}`;
  } catch (e) {
    return '';
  }
};

/** Every value the legend and the "Show values" list offer: visible first, then hidden ones. */
export const filterItems = data => {
  const visible = (
    isBarShape(data?.viz_type)
      ? (data.series || []).map(s => ({
          value: s.value,
          count: (s.counts || []).reduce((sum, n) => sum + n, 0),
        }))
      : data?.values || []
  ).map(item => ({ ...item, hidden: false }));
  const hidden = (data?.hidden || []).map(item => ({ ...item, hidden: true }));
  const colors = colorMap(valuesIn(data));
  return [...visible, ...hidden].map(item => ({
    ...item,
    color: colors[item.value],
  }));
};

export const hasOther = data =>
  isBarShape(data?.viz_type) ? !!data?.other : (data?.other_count || 0) > 0;

export const isEmpty = data => !data || Number(data.total) === 0;

/** 'none' | 'empty' (no rows in the range) | 'allHidden' (rows exist but every value is hidden). */
export const emptyReason = data => {
  if (!isEmpty(data)) return 'none';
  return (data?.hidden || []).some(item => item.count > 0)
    ? 'allHidden'
    : 'empty';
};

/** Tiles for a metric chart: one per visible value, plus Other when the backend folded some. */
export const metricTiles = data => {
  const colors = colorMap(valuesIn(data));
  const tiles = (data?.values || []).map(item => ({
    label: item.value,
    total: item.count,
    color: colors[item.value],
  }));
  if ((data?.other_count || 0) > 0) {
    tiles.push({
      label: OTHER_LABEL,
      total: data.other_count,
      color: OTHER_COLOR,
      isOther: true,
    });
  }
  return tiles;
};

/** chart.js 2.x `data` for a pie or donut, a grouped bar, or an area (a filled line per series). */
export const buildChartData = (vizType, data) => {
  if (!data) return { labels: [], datasets: [] };

  if (isBarShape(vizType)) {
    const labels = (data.buckets || []).map(b =>
      formatBucket(b, data.interval)
    );
    const isArea = vizType === 'area' || vizType === 'spark_area';
    const bar = (label, counts, color) => ({
      label,
      data: counts,
      backgroundColor: color,
      borderWidth: 0,
      maxBarThickness: 12,
      barPercentage: 0.9,
      categoryPercentage: 0.8,
    });
    // The fill fades from the series colour to transparent (see the gradient plugin).
    const area = (label, counts, color) => ({
      label,
      data: counts,
      borderColor: color,
      backgroundColor: color,
      gradientColor: color,
      fill: true,
      lineTension: 0.35,
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 3,
    });
    const draw = isArea ? area : bar;
    const colors = colorMap(valuesIn(data));
    const datasets = (data.series || []).map(s =>
      draw(s.value, s.counts, colors[s.value])
    );
    if (data.other) {
      datasets.push(draw(OTHER_LABEL, data.other.counts, OTHER_COLOR));
    }
    return { labels, datasets };
  }

  const values = data.values || [];
  const labels = values.map(v => v.value);
  const counts = values.map(v => v.count);
  const colorByValue = colorMap(valuesIn(data));
  const colors = values.map(v => colorByValue[v.value]);
  if ((data.other_count || 0) > 0) {
    labels.push(OTHER_LABEL);
    counts.push(data.other_count);
    colors.push(OTHER_COLOR);
  }
  return {
    labels,
    datasets: [
      {
        data: counts,
        backgroundColor: colors,
        borderColor: '#FFFFFF',
        borderWidth: 2,
      },
    ],
  };
};

/**
 * Legend click to new `hidden_values`. Never hides the last visible value (the chart would be
 * blank and the backend would return an empty result).
 */
export const toggleHiddenValue = (hiddenValues, value, visibleCount) => {
  const key = String(value);
  if (hiddenValues.includes(key)) {
    return hiddenValues.filter(item => item !== key);
  }
  if (visibleCount <= 1) return hiddenValues;
  return [...hiddenValues, key];
};

/** Same inboxes, in any order. */
export const sameInboxIds = (a, b) =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');

/**
 * Metrics versus values. In `value` mode (the default) a chart is one metric and each bar, slice
 * or tile is one of its values. In `key` mode a chart compares several metrics and each bar,
 * slice or tile is one metric. The data shapes are the same, so only what the entries mean (and
 * what can be hidden) changes.
 */
export const isKeyMode = chart => chart?.series_by === 'key';

/** The metrics a chart reads: its list in `key` mode, otherwise just `metric_key`. */
export const chartKeys = chart => {
  if (Array.isArray(chart?.metric_keys) && chart.metric_keys.length) {
    return chart.metric_keys;
  }
  return chart?.metric_key ? [chart.metric_key] : [];
};

/**
 * Body for the preview call. Plain charts send exactly what they always did, so a backend
 * without the metric-comparison mode still understands them.
 */
export const previewBody = ({
  seriesBy,
  keys,
  vizType,
  timeRange,
  inboxIds,
}) => {
  const common = {
    viz_type: vizType,
    time_range: timeRange,
    inbox_ids: inboxIds || [],
  };
  if (seriesBy === 'key') return { series_by: 'key', keys, ...common };
  return { key: keys[0], ...common };
};

/**
 * Body for creating a chart, or for editing one when `current` is passed. Switching a chart
 * from comparing metrics back to one metric has to say so (`series_by: 'value'`).
 */
export const chartPayload = (
  { seriesBy, keys, title, vizType, inboxIds },
  current = null
) => {
  const common = { title, viz_type: vizType, inbox_ids: inboxIds };
  if (seriesBy === 'key') {
    return { ...common, series_by: 'key', metric_keys: keys };
  }
  const payload = { ...common, metric_key: keys[0] };
  if (current && isKeyMode(current)) payload.series_by = 'value';
  return payload;
};

/**
 * Chart to vue-grid-layout items (`i` is the numeric chart id). A chart saved smaller than the
 * minimum card is drawn at the minimum; the next save writes it back.
 */
export const toGridLayout = charts =>
  charts.map(c => {
    const w = Math.max(c.layout?.w ?? 6, MIN_CARD_W);
    const x = Math.min(c.layout?.x ?? 0, 12 - w);
    return {
      i: c.id,
      x,
      y: c.layout?.y ?? 0,
      w,
      h: Math.max(c.layout?.h ?? 6, MIN_CARD_H),
    };
  });

/** Layout entries (`{ id, x, y, w, h }`) from `next` that differ from `saved`. */
export const changedLayouts = (saved, next) => {
  const before = {};
  saved.forEach(item => {
    before[item.id] = item;
  });
  return next.filter(item => {
    const old = before[item.id];
    return (
      !old ||
      old.x !== item.x ||
      old.y !== item.y ||
      old.w !== item.w ||
      old.h !== item.h
    );
  });
};

/** New charts go under everything else (the backend would also default to the bottom). */
export const nextLayoutSlot = (charts, cols = 12, width = 6, height = 6) => {
  const w = Math.max(width, MIN_CARD_W);
  const h = Math.max(height, MIN_CARD_H);
  const bottom = charts.reduce(
    (max, item) => Math.max(max, (item.layout?.y ?? 0) + (item.layout?.h ?? 0)),
    0
  );
  // First free spot, top to bottom and left to right (the grid does not pull cards up, so
  // "below everything" would leave gaps).
  const taken = (x, y) =>
    charts.some(item => {
      const l = item.layout || {};
      return (
        x < (l.x ?? 0) + (l.w ?? 0) &&
        x + w > (l.x ?? 0) &&
        y < (l.y ?? 0) + (l.h ?? 0) &&
        y + h > (l.y ?? 0)
      );
    });
  for (let y = 0; y <= bottom; y += 1) {
    for (let x = 0; x + w <= cols; x += 1) {
      if (!taken(x, y)) return { x, y: Math.min(y, 1000), w, h };
    }
  }
  return { x: 0, y: Math.min(bottom, 1000), w, h };
};

const ERROR_KEYS = {
  admin_required: 'ADMIN_REQUIRED',
  chart_not_found: 'CHART_NOT_FOUND',
  chart_limit_reached: 'CHART_LIMIT',
  range_too_large: 'RANGE_TOO_LARGE',
  too_many_buckets: 'RANGE_TOO_LARGE',
  invalid_time_range: 'INVALID_STATE',
  invalid_tz: 'INVALID_STATE',
  invalid_layout: 'INVALID_STATE',
  invalid_metric_keys: 'INVALID_STATE',
  query_timeout: 'QUERY_TIMEOUT',
};

/** i18n key under CUSTOM_ANALYTICS.ERRORS for a failed request. */
export const errorKeyFor = error => {
  const status = error?.response?.status;
  const code = error?.response?.data?.detail?.code;
  if (code && ERROR_KEYS[code]) return ERROR_KEYS[code];
  if (status === 403) return 'FORBIDDEN';
  if (status === 504) return 'QUERY_TIMEOUT';
  if (status === 502 || status === 503) return 'UNAVAILABLE';
  if (status === 422) return 'INVALID_STATE';
  // The proxy forwards the backend's 404, so a missing route means it is not deployed yet.
  if (status === 404) return 'UNAVAILABLE';
  return 'GENERIC';
};

/** Short technical detail for a failed request, e.g. `HTTP 404 · chart_not_found`. */
export const errorDetailFor = error => {
  const status = error?.response?.status;
  if (!status) return error?.message || '';
  const data = error.response.data;
  const code =
    data?.detail?.code || (typeof data?.error === 'string' ? data.error : '');
  return code ? `HTTP ${status} · ${code}` : `HTTP ${status}`;
};

export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (e) {
    return undefined;
  }
};
